import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChatBubbleIcon, UnstyledButton } from '@/shared/ui';
import { SocialUnseenDot } from '../SocialUnseenDot';

interface SocialChatLauncherProps {
  onClick: () => void;
  /** Shows a small red dot when this client has an inbound update the
   * viewer hasn't opened the chat to see yet. */
  hasUnseenUpdate?: boolean;
}

/**
 * Round chat-bubble launcher fixed to the viewport corner, matching the
 * common market pattern (Messenger/Intercom/WhatsApp widgets). Opens the
 * floating Social panel; hidden while that panel is open (its own header
 * provides minimize/close from there).
 *
 * Not on the phone. There the corner already belongs to the client's action
 * bar, which the bubble sat on top of, and the bar carries the same action
 * under a label — which the coming assistant chat makes necessary, since two
 * speech bubbles on one screen would open two different conversations.
 */
export function SocialChatLauncher({ onClick, hasUnseenUpdate }: SocialChatLauncherProps) {
  const { t } = useTranslation('features/clients');

  return createPortal(
    <UnstyledButton
      type="button"
      aria-label={t('leadOrigin.openChat')}
      data-testid="social-chat-launcher"
      onClick={onClick}
      className="max-md:hidden fixed bottom-[var(--space-4)] right-[var(--space-4)] z-[10001] w-12 h-12 rounded-full shadow-lg flex items-center justify-center bg-[var(--button-primary-bg)] text-[var(--button-primary-text)] hover:bg-[var(--button-primary-hover-bg)] active:bg-[var(--button-primary-active-bg)] transition-transform hover:scale-105"
    >
      <ChatBubbleIcon width={20} height={20} />
      <SocialUnseenDot show={Boolean(hasUnseenUpdate)} />
    </UnstyledButton>,
    document.body,
  );
}
